import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { DateTime } from 'luxon';

import authService from '../../services/AuthService';
import { convertToCSV, downloadCSV } from '../../utils/fileExportUtil';

import SelectOrderedSubList from '../SelectOrderedSubList';
import Modal from '../Modal';
import Drawer from '../Drawer';
import Input from '../Input';
import Button from '../Button';
import ListScreenSkeleton from './ListScreenSkeleton';
import { 
  DEFAULT_PAGE_SIZE, 
  SORT_DIRECTION_ASCENDNG, 
  SORT_DIRECTION_DESCENDING,
  DATATYPE_DATETIME,
  DATATYPE_DATE,
  DATATYPE_TIME,
  DATATYPE_BOOLEAN_YES_NO 
} from '../dataTable/constants';
import DataTable from '../dataTable/DataTable';
import { getColumnVisibility, resetDisplayColumns, saveDisplayColumns } from './columnUtils';
import { PERMISSION_PREFIX_CREATE, PERMISSION_PREFIX_DELETE, PERMISSION_PREFIX_UPDATE } from '../../dao/dbConstants';
import './ListScreen.css';

export const VIEW_TYPE_SCREEN = 'Screen';
export const VIEW_TYPE_MODAL = 'Modal';
export const VIEW_TYPE_DRAWER = 'Drawer';

const DataListView = ({
  entityName,
  heading,
  subheading,
  modalHeading,
  data = [],
  availableColumns = [], 
  displayColumns = [], 
  customColumns = [], 
  totalRecords = 0,
  currentPage = 1,
  pageSize = DEFAULT_PAGE_SIZE,
  orderBy = '',
  sortDirection = SORT_DIRECTION_ASCENDNG,
  isLoading = false,
  onPageChange = () => {},
  onPageSizeChange = () => {},
  onSort = () => {},
  onSearch = () => {},
  onFilterChange = () => {},
  filterValues = {},
  searchQuery = '',
  onSearchQueryChange = () => {},
  onDelete = () => {},
  onUpdateRow = () => {},
  onExport = null,
  showAddButton = true,
  enableExport = false,
  showSearch = false,
  addScreenType = VIEW_TYPE_SCREEN,
  editScreenType = VIEW_TYPE_SCREEN,
  addScreen,
  editScreen,
  rowActions = [],
  idColumn = 'id',
  addScreenAddtionalParams = {},
  editScreenAddtionalParams = {},
  onDrawerClose: onDrawerCloseCallback,
  searchFilters = [],
  tiles = [],
  tileData = {},
  onRecordSaved = () => {},
  onRowClick = null,
  enableRowClick = false,
  customHeaderActions = null,
  emptyStateConfig = null,
  usageLimitConfig = null,
  textOverflowBehavior = 'ellipsis',
  isFilterApplied = null,
}) => {
  const navigate = useNavigate();
  const userId = useSelector((state) => state.auth.user?.id);
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [drawerContent, setDrawerContent] = useState(null);
  const [editId, setEditId] = useState(null);
  
  const [localDisplayColumns, setLocalDisplayColumns] = useState(displayColumns);
  const [isDisplayColumnModalOpen, setDisplayColumnModalOpen] = useState(false);
  const [checkboxesState, setCheckboxesState] = useState({});
  const [isSelectAllChecked, setIsSelectAllChecked] = useState(false);
  const [tooltip, setTooltip] = useState({ visible: false, content: '', top: 0, left: 0 });

  useEffect(() => {
    setLocalDisplayColumns(displayColumns);
  }, [displayColumns]);

  const columnsToUse = localDisplayColumns;
  const columnVisibility = getColumnVisibility(entityName);
  const totalPages = Math.ceil(totalRecords / pageSize);
  const isLimitReached = usageLimitConfig && !usageLimitConfig.isUnlimited && usageLimitConfig.used >= usageLimitConfig.limit;
  
  const onAddClick = () => {
    if (isLimitReached) return;

    if (addScreenType === VIEW_TYPE_MODAL) {
      setIsModalOpen(true);
      setIsAdding(true);
    } else if (addScreenType === VIEW_TYPE_DRAWER) {
      setDrawerContent(
        React.createElement(addScreen, {
          id: null,
          ...addScreenAddtionalParams,
          afterSave: handleRecordSaved,
          onCancel: handleDrawerClose,
        })
      );
      setIsDrawerOpen(true);
    } else {
      if (typeof addScreen === 'function') {
        addScreen();
      } else {
        navigate(addScreen);
      }
    }
  };

  const onEditLinkClick = (record) => {
    if (editScreenType === VIEW_TYPE_MODAL) {
      setEditId(record[idColumn]);
      setIsAdding(true);
      setIsModalOpen(true);
    } else if (editScreenType === VIEW_TYPE_DRAWER) {
      setDrawerContent(
        React.createElement(editScreen, {
          id: record[idColumn],
          ...editScreenAddtionalParams,
          afterSave: handleRecordSaved,
          onCancel: handleDrawerClose,
        })
      );
      setIsDrawerOpen(true);
    } else {
      const endPoint = editScreen + record[idColumn];
      navigate(endPoint);
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setIsDrawerOpen(false);
    setIsAdding(false);
    setEditId(null);
  };

  const handleDrawerClose = () => {
    if (onDrawerCloseCallback) {
      onDrawerCloseCallback();
    }
    setIsDrawerOpen(false);
    setDrawerContent(null);
    setIsAdding(false);
    setEditId(null);
  };

  const handleRecordSaved = (entityName, record) => {
    handleModalClose();
    handleDrawerClose();
    onRecordSaved(entityName, record);
  };

  const handleSort = (field) => {
    let newSortDirection = SORT_DIRECTION_ASCENDNG;
    if (orderBy === field) {
      newSortDirection = sortDirection === SORT_DIRECTION_ASCENDNG ? SORT_DIRECTION_DESCENDING : SORT_DIRECTION_ASCENDNG;
    }
    onSort(field, newSortDirection);
  };

  const handlePageSizeChange = (event) => {
    onPageSizeChange(parseInt(event.target.value));
  };

  const handleFilterValueChange = (name, value) => {
    const updatedFilterValues = { ...filterValues, [name]: value };
    searchFilters.forEach((filter) => {
      if (filter.dependsOn?.includes(name)) {
        updatedFilterValues[filter.name] = '';
      }
    });
    onFilterChange(updatedFilterValues);
  };

  const showSelectDisplayColumnModal = () => {
    setDisplayColumnModalOpen(true);
  };

  const onDisplayColumnChange = (newDisplayColumns) => {
    setLocalDisplayColumns(newDisplayColumns);
    setDisplayColumnModalOpen(false);
    const columnsToSave = newDisplayColumns.filter(col => !col.isCustom);
    saveDisplayColumns(userId, entityName, columnsToSave);
  };

  const onDisplayColumnReset = () => {
    setDisplayColumnModalOpen(false);
    resetDisplayColumns(userId, entityName);
    window.location.reload(); 
  };

  const onCheckboxStateChange = (rowId) => {
    setCheckboxesState((prevState) => ({ ...prevState, [rowId]: !prevState[rowId] }));
  };

  const onSelectAllCheckboxStateChange = () => {
    setIsSelectAllChecked((prevState) => !prevState);
    const newCheckboxesState = data.reduce((acc, row) => {
      acc[row.id] = !isSelectAllChecked;
      return acc;
    }, {});
    setCheckboxesState(newCheckboxesState);
  };

  const handleExport = async () => {
    if (onExport) {
      setIsExporting(true);
      try {
        await onExport();
      } finally {
        setIsExporting(false);
      }
    } else if (enableExport) {
      setIsExporting(true);
      try {
        const csvData = convertToCSV(data, localDisplayColumns);
        downloadCSV(csvData, entityName);
      } finally {
        setIsExporting(false);
      }
    }
  };

  const getCellValue = (row, column) => {
    if (column.component) {
      return null;
    }
    
    if (column.valueGetter && typeof column.valueGetter === 'function') {
      return column.valueGetter(row);
    }
    
    if (column.field) return row[column.field];
    if (column.key) return row[column.key];
    if (column.value) return row[column.value];
    
    return null;
  };

  const formatCellData = (displayColumn, obj) => {
    if (obj === null || obj === undefined || obj === '') {
      return '';
    }
    if (displayColumn.dataType === DATATYPE_BOOLEAN_YES_NO) {
      return obj === true ? 'Yes' : 'No';
    }
    
    if (displayColumn.dataType === DATATYPE_DATE) {
      return DateTime.fromISO(obj.substring(0, 10)).toFormat(displayColumn.format || 'LL/dd/yyyy');
    }
    if (displayColumn.dataType === DATATYPE_TIME) {
      return DateTime.fromISO(obj).toFormat(displayColumn.format || 'hh:mm:ss a');
    }
    if (displayColumn.dataType === DATATYPE_DATETIME) {
      return DateTime.fromISO(obj).toFormat(displayColumn.format || 'LL/dd/yyyy hh:mm:ss a');
    }

    return obj;
  };

  const renderCellContent = (row, column, rowIndex) => {
    if (column.component) {
      const Component = column.component;
      const props = column.componentProps 
        ? (typeof column.componentProps === 'function' ? column.componentProps(row, rowIndex) : column.componentProps)
        : {};
      
      return <Component {...props} row={row} rowIndex={rowIndex} />;
    }

    let value = getCellValue(row, column);
    if (column.formatter && typeof column.formatter === 'function') {
      value = column.formatter(value, row);
    } else {
      value = formatCellData(column, value);
    }

    return value !== null && value !== undefined ? value : '';
  };

  const handleRowActionClick = async (e, action, row) => {
    e.stopPropagation();
    const handler = action.handler || action.onClick;
    if (handler) {
      try {
        const result = await handler(e, row);
        if (result && typeof result === 'object' && result.id) {
          onUpdateRow(result);
        }
      } catch {
        // Errors are usually handled by toasts inside the handlers
      }
    }
  };

  const handleCellMouseEnter = (e, content) => {
    if (textOverflowBehavior !== 'ellipsis' || !content) return;

    const cell = e.currentTarget;
    if (cell.scrollWidth > cell.clientWidth) {
      const rect = cell.getBoundingClientRect();
      setTooltip({
        visible: true,
        content: content,
        top: rect.bottom + 5,
        left: rect.left
      });
    }
  };

  const handleCellMouseLeave = () => {
    if (tooltip.visible) {
      setTooltip(prev => ({ ...prev, visible: false }));
    }
  };

  const renderCustomTable = () => {
    if (isLoading) {
      return <ListScreenSkeleton />;
    }

    const hasData = data && data.length > 0;
    const hasEditPermission = authService.hasEntityPermission(entityName, PERMISSION_PREFIX_UPDATE);
    const hasDeletePermission = authService.hasEntityPermission(entityName, PERMISSION_PREFIX_DELETE);
    const showActionColumn = rowActions.length > 0 || hasDeletePermission;
    const colSpan = columnsToUse.length + (showActionColumn ? 1 : 0);
    const cellTextClass = textOverflowBehavior === 'ellipsis' ? 'cell-text-ellipsis' : 'cell-text-wrap';

    return (
      <div className="data-table">
        <table>
          <thead>
            <tr>
              {columnsToUse.map((column) => (
                <th
                  key={column.key || column.value}
                  className={column.sortable !== false ? 'sortable' : ''}
                  style={{
                    width: column.width,
                    textAlign: column.align || 'left',
                  }}
                  onClick={() => {
                    if (column.sortable !== false) {
                      handleSort(column.key || column.value);
                    }
                  }}
                >
                   <div 
                    className="th-content" 
                    style={{ justifyContent: column.align === 'center' ? 'center' : column.align === 'right' ? 'flex-end' : 'flex-start' }}
                  >
                    {column.header || column.label}
                    {column.sortable !== false && orderBy === (column.key || column.value) && (
                      <i className={`bx sort-icon ${sortDirection === SORT_DIRECTION_ASCENDNG ? 'bx-chevron-up' : 'bx-chevron-down'}`} />
                    )}
                  </div>
                </th>
              ))}
              {showActionColumn && (
                <th style={{ width: '100px', textAlign: 'center' }}></th>
              )}
            </tr>
          </thead>
          <tbody>
            {!hasData ? (
              <tr>
                <td colSpan={colSpan}>
                  No records to display
                </td>
              </tr>
            ) : (
              data.map((row, rowIndex) => (
                <tr
                  key={row[idColumn] || rowIndex}
                  onClick={() => {
                    if (enableRowClick && onRowClick) {
                      onRowClick(row);
                    }
                  }}
                  style={{ cursor: enableRowClick && onRowClick ? 'pointer' : 'default' }}
                >
                  {columnsToUse.map((column) => {
                    const isLinked = hasEditPermission && column.isLinkedToEditScreen;
                    const cellContent = renderCellContent(row, column, rowIndex);
                    const cellContentString = React.isValidElement(cellContent) ? null : cellContent;

                    return (
                      <td
                        key={column.key || column.value}
                        className={`${isLinked ? 'edit-link' : ''} ${cellTextClass}`}
                        style={{
                          textAlign: column.align || 'left',
                        }}
                        onClick={(e) => {
                          if (isLinked) {
                            e.stopPropagation();
                            onEditLinkClick(row);
                          }
                        }}
                        onMouseEnter={(e) => handleCellMouseEnter(e, cellContentString)}
                        onMouseLeave={handleCellMouseLeave}
                      >
                        {cellContent}
                      </td>
                    );
                  })}
                  {showActionColumn && (
                    <td className="actions">
                      <div className="actions-wrapper">
                        {rowActions
                          .filter(action => !action.displayCriteriaFunction || action.displayCriteriaFunction(row))
                          .map((action, actionIndex) => (
                          <div
                            key={actionIndex}
                            className="row-action"
                            onClick={(e) => handleRowActionClick(e, action, row)}
                            title={action.tooltipText || action.label}
                          >
                            {(action.class || action.icon) && <i className={action.class || action.icon}></i>}
                          </div>
                        ))}
                        {hasDeletePermission && (
                          <div
                            className="row-action bx bx-trash"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (window.confirm('Are you sure you want to delete this record?')) {
                                onDelete(row);
                              }
                            }}
                            title="Delete"
                          >
                          </div>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    );
  };

  const hasCreatePermission = authService.hasEntityPermission(entityName, PERMISSION_PREFIX_CREATE);
  const isFilteringActive = isFilterApplied !== null 
    ? isFilterApplied 
    : (searchQuery && searchQuery.length > 0) || (filterValues && Object.values(filterValues).some(v => !!v));

  const showCentralizedEmptyState = !isLoading && totalRecords === 0 && !isFilteringActive && !!emptyStateConfig;
  const shouldShowFilters = showSearch || (searchFilters && searchFilters.length > 0);
  const showFilterSection = !showCentralizedEmptyState && (
    shouldShowFilters ||
    (customHeaderActions && !heading)
  );
  
  const showHeaderRow = !showCentralizedEmptyState && (!!heading || enableExport || (showAddButton && hasCreatePermission) || customHeaderActions);

  const isPureCustom = !columnVisibility && availableColumns.length === 0 && customColumns.length > 0;
  const hasCustomComponents = columnsToUse.some(col => !!col.component);
  const hasStandardColumnConfig = !!columnVisibility;
  const shouldUseCustomTable = isPureCustom || hasCustomComponents || !hasStandardColumnConfig;
  
  if (showCentralizedEmptyState) {
    return (
      <div className="client-area table-container">
        <div className="central-empty-state">
           {emptyStateConfig.icon && (
             <div className="empty-state-icon">
               {emptyStateConfig.icon}
             </div>
           )}
           {emptyStateConfig.title && (
             <h3 className="empty-state-title">{emptyStateConfig.title}</h3>
           )}
           {emptyStateConfig.description && (
             <p className="empty-state-description">{emptyStateConfig.description}</p>
           )}
           {emptyStateConfig.actions && (
             <div className="empty-state-actions">
               {emptyStateConfig.actions}
             </div>
           )}
        </div>
        
        {isModalOpen && (
          <Modal
            isOpen={isModalOpen}
            onClose={handleModalClose}
            title={(!!editId ? 'Edit ' : 'Add ') + modalHeading}
            showTitleBar={true}
            {...(!!editId ? editScreenAddtionalParams : addScreenAddtionalParams)}
          >
            {isAdding && React.createElement(addScreen, { id: editId, afterSave: handleRecordSaved, onCancel: handleModalClose })}
          </Modal>
        )}
        {isDrawerOpen && (
          <Drawer isOpen={isDrawerOpen} onClose={handleDrawerClose} heading={modalHeading}>
            {drawerContent}
          </Drawer>
        )}
      </div>
    );
  }

  return (
    <>
      {showHeaderRow && (
        <div className={`heading-row${subheading ? ' heading-row-with-subtext' : ''}`}>
          {heading && (
            <div className="heading-text">
              <h2>{heading}</h2>
              {subheading && <p className="heading-subtext">{subheading}</p>}
            </div>
          )}
          <div className="header-actions">
            {enableExport && (
              <Button
                className="secondary export"
                onClick={handleExport}
                icon="bx bx-export"
                disabled={isExporting}
              >
                {isExporting ? 'Exporting...' : 'Export'}
              </Button>
            )}
            
            {usageLimitConfig && !usageLimitConfig.isUnlimited && (
              <span className="usage-stats">
                {usageLimitConfig.used} / {usageLimitConfig.limit} used
                {isLimitReached && (
                  <Link 
                    to="/subscription" 
                    className="upgrade-link"
                  >
                    Upgrade
                  </Link>
                )}
              </span>
            )}

            {showAddButton && hasCreatePermission && (
              <Button 
                className={`primary add ${isLimitReached ? 'disabled' : ''}`} 
                onClick={onAddClick} 
                icon="bx bx-plus"
                disabled={isLimitReached}
                title={isLimitReached ? 'Usage limit reached' : 'Add New'}
              >
                Add New
              </Button>
            )}
            {customHeaderActions}
          </div>
        </div>
      )}

      <div className="client-area table-container">
        {showFilterSection && (
          <div className="filter-section">
            
            {(customHeaderActions && !heading && !showHeaderRow) && (
              <div className="custom-header-actions-section">
                {customHeaderActions}
              </div>
            )}

            <div className="filter-inputs">
              {showSearch && (
                <div className="filter-item">
                  <input
                    type="text"
                    className="searchTerm"
                    placeholder="Search..."
                    value={searchQuery ?? ''}
                    onChange={(event) => onSearchQueryChange(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' && !isLoading) {
                        event.preventDefault();
                        onSearch();
                      }
                    }}
                  />
                </div>
              )}

              {searchFilters.map((filter) => (
                <div key={filter.name} className="filter-item">
                  <Input
                    type={filter.type}
                    label={filter.label}
                    name={filter.name}
                    value={filterValues?.[filter.name] ?? filter.defaultValue ?? ''}
                    isRequired={filter.isRequired}
                    isDisabled={filter.isDisabled}
                    includeNoneOption={filter.includeNoneOption}
                    style={filter.style ?? {}}
                    toolTipText={filter.toolTipText}
                    optionsFunction={filter.optionsFunction}
                    showSearch={filter.showSearch}
                    className={filter.className}
                    showDots={filter.showDots}
                    onChange={(value) => handleFilterValueChange(filter.name, value)}
                    optionsFunctionParams={{ filterValues }}
                  />
                </div>
              ))}
              {(showSearch || !!searchFilters?.length) && (
                <div className="filter-item">
                  <button className="button search" disabled={isLoading} onClick={onSearch}>
                    {isLoading ? 'Searching...' : 'Search'}
                  </button>
                </div>
              )}
            </div>
            
            {(availableColumns.length > 0) && (
              <button
                disabled={false}
                title="Edit Columns"
                className="button filter-columns"
                onClick={() => {
                  showSelectDisplayColumnModal();
                }}
              >
                <i className="bx bx-columns" />
              </button>
            )}
          </div>
        )}

        {tiles.length > 0 && (
          <div className="tiles-section">
            {tiles.map((tile) => (
              <div key={tile.title} className="tile">
                <div className="tile-title">{tile.title}</div>
                <div className="tile-value">{tileData[tile.title] ?? '...'}</div>
              </div>
            ))}
          </div>
        )}

        {isLoading ? (
          <ListScreenSkeleton />
        ) : shouldUseCustomTable ? (
          renderCustomTable()
        ) : (
          <DataTable
            displayColumns={localDisplayColumns}
            columnVisibility={columnVisibility}
            data={data}
            onSort={handleSort}
            currentSortOrderAttribute={orderBy}
            isCurrentSortOrderAscending={sortDirection === SORT_DIRECTION_ASCENDNG}
            checkboxesState={checkboxesState}
            onSelectAllCheckboxStateChange={onSelectAllCheckboxStateChange}
            onCheckboxStateChange={onCheckboxStateChange}
            isSelectAllChecked={isSelectAllChecked}
            hasEditPermission={authService.hasEntityPermission(entityName, PERMISSION_PREFIX_UPDATE)}
            onEditLinkClick={onEditLinkClick}
            rowActions={rowActions}
            emptyTableMessage={`No records to display`}
            isProcessing={isLoading}
            hasDeletePermission={authService.hasEntityPermission(entityName, PERMISSION_PREFIX_DELETE)}
            onDelete={onDelete}
            onUpdateRow={onUpdateRow}
            textOverflowBehavior={textOverflowBehavior}
            enableRowClick={enableRowClick}
            onRowClick={onRowClick}
          />
        )}
        {!!data?.length ? (
          <div className="pagination-section">
            <div className="pagination-info">
              Showing {(currentPage - 1) * pageSize + 1} - {Math.min(currentPage * pageSize, totalRecords)} of{' '}
              {totalRecords}
              <select value={pageSize} onChange={handlePageSizeChange}>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
            <div className="pagination-controls">
              <span
                onClick={() => {
                  if (currentPage > 1) {
                    onPageChange(1);
                  }
                }}
              >
                <i className={`bx bx-first-page${currentPage === 1 ? ' disabled' : ''}`} />
              </span>
              <span
                onClick={() => {
                  if (currentPage > 1) {
                    onPageChange(currentPage - 1);
                  }
                }}
              >
                <i className={`bx bx-chevron-left${currentPage === 1 ? ' disabled' : ''}`} />
              </span>
              <input
                type="number"
                value={currentPage}
                max={totalPages}
                onChange={(e) => onPageChange(parseInt(e.target.value, 10))}
              />
              <span
                onClick={() => {
                  if (currentPage < totalPages) {
                    onPageChange(currentPage + 1);
                  }
                }}
              >
                <i className={`bx bx-chevron-right${currentPage === totalPages ? ' disabled' : ''}`} />
              </span>
              <span
                onClick={() => {
                  if (currentPage < totalPages) {
                    onPageChange(totalPages);
                  }
                }}
              >
                <i className={`bx bx-last-page${currentPage === totalPages ? ' disabled' : ''}`} />
              </span>
            </div>
          </div>
        ) : null}
        
        <div 
          className={`custom-tooltip ${tooltip.visible ? 'visible' : ''}`}
          style={{ top: tooltip.top, left: tooltip.left }}
        >
          {tooltip.content}
        </div>

        <SelectOrderedSubList
          title="Choose which columns you see"
          availableListLabel="Available"
          selectedListLabel="Selected"
          isOpen={isDisplayColumnModalOpen}
          allItems={availableColumns}
          selectedItems={localDisplayColumns}
          displayKey="label" 
          valueKey="value"
          onSave={(selectedDisplayColumns) => {
            onDisplayColumnChange(selectedDisplayColumns);
          }}
          onCancel={() => {
            setDisplayColumnModalOpen(false);
          }}
          onReset={() => {
            onDisplayColumnReset();
          }}
        />
      </div>

      {isModalOpen && (
        <Modal
          isOpen={isModalOpen}
          onClose={handleModalClose}
          title={(!!editId ? 'Edit ' : 'Add ') + modalHeading}
          showTitleBar={true}
          {...(!!editId ? editScreenAddtionalParams : addScreenAddtionalParams)}
        >
          {isAdding && React.createElement(addScreen, { id: editId, afterSave: handleRecordSaved, onCancel: handleModalClose })}
        </Modal>
      )}
      {isDrawerOpen && (
        <Drawer isOpen={isDrawerOpen} onClose={handleDrawerClose} heading={modalHeading}>
          {drawerContent}
        </Drawer>
      )}
    </>
  );
};

export default DataListView;